/**
 * Cookie Consent Banner
 * Shows a banner at the bottom of the page for cookie consent
 */

import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Link,
  Container,
  HStack,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { MdCookie } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../../hooks/useCookieConsent';

const CookieBanner = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, acceptEssential, rejectAll, isLoading } =
    useCookieConsent();

  // Colors - All hooks must be called before any conditional returns
  const bgColor = 'var(--bg-primary)';
  const borderColor = 'var(--border-color)';
  const textColor = 'var(--text-primary)';
  const mutedColor = 'var(--text-secondary)';
  const hoverBg = 'var(--bg-secondary)';

  // Don't show while loading or if consent already given
  if (isLoading || !showBanner) {
    return null;
  }

  return (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex="banner"
      bg={bgColor}
      borderTop="1px"
      borderColor={borderColor}
      boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)"
      py={{ base: 4, md: 5 }}
    >
      <Container maxW="container.xl">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          gap={4}
        >
          {/* Cookie Icon and Text */}
          <Flex align="flex-start" gap={3} flex="1">
            <Icon
              as={MdCookie}
              w={6}
              h={6}
              color="brand.500"
              flexShrink={0}
              mt={0.5}
            />
            <VStack align="flex-start" spacing={1}>
              <Text fontWeight="semibold" color={textColor}>
                {t('cookies.banner.title', 'Usamos cookies')}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                {t(
                  'cookies.banner.description',
                  'Utilizamos cookies esenciales para el funcionamiento del sitio y opcionales para mejorar tu experiencia.'
                )}{' '}
                <Link
                  as={RouterLink}
                  to="/cookies"
                  color="brand.500"
                  textDecoration="underline"
                  _hover={{ color: 'brand.600' }}
                >
                  {t('cookies.banner.learnMore', 'Ver Política de Cookies')}
                </Link>
              </Text>
            </VStack>
          </Flex>

          {/* Buttons */}
          <HStack
            spacing={3}
            flexShrink={0}
            justify={{ base: 'stretch', md: 'flex-end' }}
            w={{ base: '100%', md: 'auto' }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={rejectAll}
              color={mutedColor}
              _hover={{ bg: hoverBg }}
              flex={{ base: 1, md: 'none' }}
            >
              {t('cookies.banner.reject', 'Rechazar')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={acceptEssential}
              borderColor={borderColor}
              flex={{ base: 1, md: 'none' }}
            >
              {t('cookies.banner.essentialOnly', 'Solo esenciales')}
            </Button>
            <Button
              colorScheme="brand"
              size="sm"
              onClick={acceptAll}
              flex={{ base: 1, md: 'none' }}
            >
              {t('cookies.banner.acceptAll', 'Aceptar todo')}
            </Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default CookieBanner;
